import { useEffect, useState } from 'react'
import { Container, Row, Col, Card, Modal, Form, Button, Alert } from 'react-bootstrap'
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from 'react-redux'
import Swal from 'sweetalert2'
import { toastSuccess, toastError } from '../../../components/swal/toast'
import { useAuth } from '../../../components/auth/useAuth'
import {
    getNotifications,
    getNoticeboard,
    getSentMessages,
    getThread,
    resetThread,
    markNotificationRead,
    markAllNotificationsRead,
    sendMessage,
    resetMessage,
    deleteMessage
} from '../../../actions/communicationActions'
import { getStudents } from '../../../actions/studentActions'
import { getGroups } from '../../../actions/classActions'
import AxiosControl from '../../../components/axios/AxiosControl'
const Axios = AxiosControl()

const formatDate = (d) => {
    if (!d) return ''
    return new Date(d).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })
}

const formatTime = (d) => {
    if (!d) return ''
    return new Date(d).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
}

export default function MessageList() {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { clientauth } = useAuth()
    const {
        notifications,
        notificationsLoading,
        unreadCount,
        noticeboard,
        noticeboardLoading,
        sentMessages,
        sentLoading,
        thread,
        threadLoading,
        messageSending,
        messageError2
    } = useSelector(state => state.communications)
    const { students } = useSelector(state => state.students)
    const { groups } = useSelector(state => state.classes)

    const [activeTab, setActiveTab] = useState('notifications')
    const [filterUnreadOnly, setFilterUnreadOnly] = useState(false)
    const [expandedId, setExpandedId] = useState(null)
    const [activeThreadItem, setActiveThreadItem] = useState(null)
    const [replyText, setReplyText] = useState('')
    const [sendContext, setSendContext] = useState(null)

    const [showComposeModal, setShowComposeModal] = useState(false)
    const [composeForm, setComposeForm] = useState({ recipientType: 'individual', recipientId: '', recipientRole: 'student', groupId: '', subject: '', message: '', attachments: [] })
    const [uploadingFiles, setUploadingFiles] = useState(false)
    const [replyAttachments, setReplyAttachments] = useState([])
    const [uploadingReplyFiles, setUploadingReplyFiles] = useState(false)
    const [searchInbox, setSearchInbox] = useState('')
    const [searchBoard, setSearchBoard] = useState('')
    const [searchSent, setSearchSent] = useState('')
    const [activeNoticeItem, setActiveNoticeItem] = useState(null)

    useEffect(() => {
        dispatch(getNotifications())
        dispatch(getNoticeboard())
        dispatch(getSentMessages())
        dispatch(getStudents())
        dispatch(getGroups())
    }, [dispatch])

    const tabs = [
        { key: 'notifications', label: t('teacher.communications.inbox') },
        { key: 'noticeboard', label: t('teacher.communications.noticeboard') },
        { key: 'sent', label: t('teacher.communications.sent') },
    ]

    const matchesSearch = (n, s) => {
        if (!s.trim()) return true
        const q = s.toLowerCase()
        return (n.subject || '').toLowerCase().includes(q) ||
            (n.message || '').toLowerCase().includes(q) ||
            (n.senderName || '').toLowerCase().includes(q) ||
            (n.recipientName || '').toLowerCase().includes(q) ||
            (n.groupName || '').toLowerCase().includes(q)
    }

    const visibleNotifications = (filterUnreadOnly ? notifications.filter(n => !n.read) : notifications).filter(n => matchesSearch(n, searchInbox))

    const handleNotificationClick = (n) => {
        setExpandedId(expandedId === n._id ? null : n._id)
        if (!n.read) dispatch(markNotificationRead(n._id))
    }

    const handleOpenThread = (threadItem) => {
        setActiveThreadItem(threadItem)
        dispatch(getThread(threadItem.threadId))
    }

    const handleCloseThread = () => {
        setActiveThreadItem(null)
        dispatch(resetThread())
    }

    const handleReplySubmit = async (e) => {
        e.preventDefault()
        if (!replyText.trim() || !activeThreadItem) return
        setSendContext('reply')
        dispatch(resetMessage())
        const ok = await dispatch(sendMessage({
            recipientId: activeThreadItem.last.sender === clientauth?.userid ? activeThreadItem.last.recipient : activeThreadItem.last.sender,
            recipientName: activeThreadItem.last.sender === clientauth?.userid ? activeThreadItem.last.senderName : activeThreadItem.last.recipientName,
            subject: activeThreadItem.last.subject,
            message: replyText,
            threadId: activeThreadItem.threadId,
            type: 'individual',
            attachments: replyAttachments
        }))
        if (ok) {
            setReplyText('')
            setReplyAttachments([])
            dispatch(getThread(activeThreadItem.threadId))
            dispatch(getSentMessages())
            dispatch(getNotifications())
        }
    }

    const handleFileSelect = async (e, target) => {
        const files = e.target.files
        if (!files || files.length === 0) return
        const formData = new FormData()
        Array.from(files).forEach(f => formData.append('files', f))
        if (target === 'reply') setUploadingReplyFiles(true)
        else setUploadingFiles(true)
        try {
            const { data, status } = await Axios.post('/api/upload/uploadfiles', formData, {
                headers: { auth: localStorage.getItem('jwt'), 'Content-Type': 'multipart/form-data' }
            })
            if (status === 200 && Array.isArray(data)) {
                if (target === 'reply') setReplyAttachments(prev => [...prev, ...data])
                else setComposeForm(prev => ({ ...prev, attachments: [...prev.attachments, ...data] }))
            }
        } catch (error) {
            toastError(t('teacher.communications.uploadError'))
        } finally {
            if (target === 'reply') setUploadingReplyFiles(false)
            else setUploadingFiles(false)
            e.target.value = ''
        }
    }

    const handleComposeSubmit = async (e) => {
        e.preventDefault()
        if (!composeForm.subject.trim() || !composeForm.message.trim()) return

        setSendContext('compose')
        dispatch(resetMessage())
        const payload = {
            subject: composeForm.subject,
            message: composeForm.message,
            attachments: composeForm.attachments,
            type: composeForm.recipientType === 'group' ? 'group' : composeForm.recipientType === 'noticeboard' ? 'noticeboard' : 'individual'
        }

        if (composeForm.recipientType === 'individual' && composeForm.recipientId) {
            payload.recipientId = composeForm.recipientId
            payload.recipientRole = composeForm.recipientRole
            const s = (students || []).find(st => String(st.id) === String(composeForm.recipientId))
            payload.recipientName = s?.name || ''
        } else if (composeForm.recipientType === 'group' && composeForm.groupId) {
            payload.groupId = composeForm.groupId
            const g = groups.find(gr => String(gr.odooGroupId || gr._id) === String(composeForm.groupId))
            payload.groupName = g?.name || ''
        }

        const ok = await dispatch(sendMessage(payload))
        if (ok) {
            toastSuccess(typeof ok === 'string' ? ok : t('teacher.communications.messageSent'))
            setShowComposeModal(false)
            setComposeForm({ recipientType: 'individual', recipientId: '', recipientRole: 'student', groupId: '', subject: '', message: '', attachments: [] })
            dispatch(getSentMessages())
            dispatch(getNotifications())
            if (payload.type === 'noticeboard') dispatch(getNoticeboard())
        }
    }

    const renderNotificationItem = (n, isNoticeboard = false) => (
        <div
            key={n._id}
            onClick={() => {
                if (isNoticeboard) {
                    setActiveNoticeItem(n)
                } else if (n.sender && String(n.sender) !== String(clientauth?.userid)) {
                    handleOpenThread({ threadId: n.threadId || n._id, last: n })
                }
            }}
            className={`border rounded-4 p-3 mb-3 communication-item`}
            style={{
                cursor: 'pointer',
                backgroundColor: n.read || isNoticeboard ? '#fff' : '#fff7ed',
                borderColor: n.read || isNoticeboard ? '#e5e7eb' : '#fdba74',
                boxShadow: n.read || isNoticeboard ? 'none' : '0 1px 3px rgba(249, 115, 22, 0.12)',
                transition: 'all 0.2s ease'
            }}
        >
            <div className='d-flex align-items-start gap-3'>
                <div className='d-flex align-items-center justify-content-center rounded-circle flex-shrink-0'
                    style={{ width: '40px', height: '40px', backgroundColor: n.read || isNoticeboard ? '#f3f4f6' : '#ffedd5' }}>
                    {isNoticeboard ? (
                        String(n.sender) === String(clientauth?.userid) ? (
                            <i className='fa-regular fa-paper-plane' style={{ color: '#9ca3af', fontSize: '16px' }}></i>
                        ) : (
                            <i className='fa-solid fa-bullhorn' style={{ color: '#9ca3af', fontSize: '16px' }}></i>
                        )
                    ) : n.read ? (
                        <i className='fa-regular fa-envelope' style={{ color: '#9ca3af', fontSize: '16px' }}></i>
                    ) : (
                        <i className='fa-solid fa-triangle-exclamation' style={{ color: '#f97316', fontSize: '16px' }}></i>
                    )}
                </div>
                <div className='flex-grow-1' style={{ minWidth: 0 }}>
                    <div className='d-flex justify-content-between align-items-center gap-2'>
                        <p className='mb-1 text-black' style={{ fontSize: '15px' }}>{n.subject}</p>
                        <div className='d-flex align-items-center gap-2 flex-shrink-0'>
                            <span className='badge rounded-pill' style={{ fontSize: '10px', backgroundColor: n.read ? '#e5e7eb' : '#dc2626', color: n.read ? '#6b7280' : '#fff', visibility: n.read || isNoticeboard ? 'hidden' : 'visible' }}>{t('teacher.communications.new')}</span>
                            {!isNoticeboard && n.sender && String(n.sender) !== String(clientauth?.userid) && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        handleOpenThread({ threadId: n.threadId || n._id, last: n })
                                    }}
                                    className='btn btn-sm btn-primary rounded-pill px-3 py-1 d-inline-flex align-items-center gap-1'
                                    style={{ fontSize: '12px' }}
                                >
                                    <i className='fa-solid fa-reply'></i> {t('teacher.communications.reply')}
                                </button>
                            )}
                        </div>
                    </div>
                    <p
                        className={`text-muted mb-1 ${expandedId === n._id ? '' : 'communication-message-clamp'}`}
                        style={{ fontSize: '14px', whiteSpace: 'pre-line' }}>
                        {n.message}
                    </p>
                    {expandedId === n._id && n.attachments && n.attachments.length > 0 && (
                        <div className='mt-2'>
                            <p className='mb-1 fw-semibold' style={{ fontSize: '12px' }}>{t('teacher.communications.attachments')}:</p>
                            {n.attachments.map((a, idx) => (
                                <div key={idx} style={{ fontSize: '13px' }}>
                                    {a.url ? <a href={a.url} target='_blank' rel='noreferrer'>{a.name || a.url}</a> : <span>{a.name}</span>}
                                </div>
                            ))}
                        </div>
                    )}
                    <p className='mb-0 text-muted' style={{ fontSize: '12px' }}>
                        {formatDate(n.createdAt)} · {formatTime(n.createdAt)}
                        {n.senderName && ` · ${n.senderName}`}
                        {n.groupName && ` · ${n.groupName}`}
                    </p>
                </div>
            </div>
        </div>
    )

    const renderSentItem = (m) => (
        <div
            key={m.threadId || m._id}
            onClick={() => handleOpenThread({ threadId: m.threadId || m._id, last: m })}
            className='border rounded-4 p-3 mb-3 communication-item'
            style={{ cursor: 'pointer', backgroundColor: '#fff', borderColor: '#e5e7eb' }}
        >
            <div className='d-flex align-items-start gap-3'>
                <div className='d-flex align-items-center justify-content-center rounded-circle flex-shrink-0'
                    style={{ width: '40px', height: '40px', backgroundColor: '#f3f4f6' }}>
                    <i className='fa-regular fa-paper-plane' style={{ color: '#9ca3af', fontSize: '16px' }}></i>
                </div>
                <div className='flex-grow-1' style={{ minWidth: 0 }}>
                    <div className='d-flex justify-content-between align-items-start gap-2'>
                        <p className='fw-semibold mb-1 text-black text-truncate' style={{ fontSize: '15px' }}>{m.subject}</p>
                    </div>
                    <p className='text-muted mb-1 communication-message-clamp' style={{ fontSize: '14px' }}>
                        {m.message}
                    </p>
                    <p className='mb-0 text-muted' style={{ fontSize: '12px' }}>
                        {formatDate(m.createdAt)} · {formatTime(m.createdAt)}
                        {m.recipientName && ` · ${m.recipientName}`}
                        {!m.recipientName && m.groupName && ` · ${t('teacher.communications.groupPrefix')} ${m.groupName}`}
                        {!m.recipientName && !m.groupName && m.type === 'noticeboard' && ` · ${t('teacher.communications.noticeboardTab')}`}
                    </p>
                </div>
                <i className='fa-solid fa-chevron-right text-muted flex-shrink-0 mt-2'></i>
            </div>
        </div>
    )

    return (
        <Container className='my-3 p-5'>
            <Row className='align-items-center'>
                <Col>
                    <h3 className='fw-bold mb-1'>{t('teacher.communications.title')}</h3>
                    <p className='text-muted mb-0'>{notifications?.length || 0} {t('teacher.communications.messagecount')}</p>
                </Col>
                <Col xs='auto' className='d-flex gap-2'>
                    <button onClick={() => setShowComposeModal(true)}
                        className='btn border rounded-pill px-4 d-inline-flex align-items-center gap-2'
                        style={{ color: '#374151', borderColor: '#d1d5db', background: '#fff' }}>
                        <i className='fa-solid fa-paper-plane'></i>
                        <span className='d-none d-md-inline'>{t('teacher.communications.newmessage')}</span>
                    </button>
                    <button onClick={() => setActiveTab('notifications')}
                        className='btn btn-primary rounded-pill px-4 d-inline-flex align-items-center gap-2'>
                        <i className='fa-regular fa-bell'></i>
                        {unreadCount} {t('teacher.communications.unreadcount')}
                    </button>
                </Col>
            </Row>

            <Row className='mt-4'>
                <Col>
                    <div className='d-flex gap-2 bg-white rounded-4 p-2 shadow-sm' style={{ maxWidth: 'fit-content' }}>
                        {tabs.map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`btn border-0 rounded-3 px-4 py-2 position-relative ${activeTab === tab.key ? 'text-black' : 'text-muted'}`}
                                style={{ fontWeight: 600 }}
                            >
                                {tab.label}
                                {tab.key === 'notifications' && unreadCount > 0 && (
                                    <span className='badge rounded-pill bg-danger ms-2' style={{ fontSize: '11px' }}>{unreadCount}</span>
                                )}
                            </button>
                        ))}
                    </div>
                </Col>
            </Row>

            {activeTab === 'notifications' && (
                <Row className='mt-4'>
                    <Col>
                        <Card className='border-0 rounded-4'>
                            <Card.Body className='p-4'>
                                <Form.Control
                                    type='text'
                                    value={searchInbox}
                                    onChange={e => setSearchInbox(e.target.value)}
                                    placeholder={t('teacher.communications.searchPlaceholder')}
                                    className='mb-3'
                                    style={{ borderRadius: '10px', maxWidth: '400px' }}
                                />
                                <div className='d-flex flex-wrap gap-2 mb-3'>
                                    <button
                                        onClick={() => setFilterUnreadOnly(false)}
                                        className={`rounded-pill invoice-filter-btn d-inline-flex align-items-center gap-1 border-0 px-3 py-1 ${!filterUnreadOnly ? 'active' : ''}`}
                                    >
                                        {t('teacher.communications.all')} ({notifications.length})
                                    </button>
                                    <button
                                        onClick={() => setFilterUnreadOnly(true)}
                                        className={`rounded-pill invoice-filter-btn d-inline-flex align-items-center gap-1 border-0 px-3 py-1 ${filterUnreadOnly ? 'active' : ''}`}
                                    >
                                        {t('teacher.communications.unread')} ({unreadCount})
                                    </button>
                                    {unreadCount > 0 && (
                                        <button
                                            onClick={() => dispatch(markAllNotificationsRead())}
                                            className='btn btn-sm border-0 ms-auto d-inline-flex align-items-center gap-2'
                                            style={{ color: '#dc2626' }}
                                        >
                                            <i className='fa-solid fa-check-double'></i>
                                            {t('teacher.communications.markallread')}
                                        </button>
                                    )}
                                </div>

                                {notificationsLoading ? (
                                    <>
                                        {[1, 2, 3].map(i => (
                                            <div key={i} className='border rounded-4 p-3 mb-3'>
                                                <div className='bg-light rounded-3' style={{ height: '14px', width: '50%' }} />
                                                <div className='bg-light rounded-3 mt-2' style={{ height: '12px', width: '90%' }} />
                                            </div>
                                        ))}
                                    </>
                                ) : visibleNotifications.length === 0 ? (
                                    <p className='text-muted text-center py-5 mb-0'>{t('teacher.communications.noNotifications')}</p>
                                ) : (
                                    visibleNotifications.map(n => renderNotificationItem(n))
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {activeTab === 'noticeboard' && (
                <Row className='mt-4'>
                    <Col>
                        <Card className='border-0 rounded-4 p-4'>
                            <Form.Control
                                type='text'
                                value={searchBoard}
                                onChange={e => setSearchBoard(e.target.value)}
                                placeholder={t('teacher.communications.searchPlaceholder')}
                                className='mb-3'
                                style={{ borderRadius: '10px', maxWidth: '400px' }}
                            />
                            {noticeboardLoading ? (
                                <>
                                    {[1, 2].map(i => (
                                        <div key={i} className='border rounded-4 p-3 mb-3'>
                                            <div className='bg-light rounded-3' style={{ height: '14px', width: '50%' }} />
                                            <div className='bg-light rounded-3 mt-2' style={{ height: '12px', width: '90%' }} />
                                        </div>
                                    ))}
                                </>
                            ) : noticeboard.length === 0 ? (
                                <p className='text-muted text-center py-5 mb-0'>{t('teacher.communications.noNoticeboard')}</p>
                            ) : (
                                noticeboard.filter(n => matchesSearch(n, searchBoard)).map(n => renderNotificationItem(n, true))
                            )}
                        </Card>
                    </Col>
                </Row>
            )}

            {activeTab === 'sent' && (
                <Row className='mt-4'>
                    <Col>
                        <Card className='border-0 rounded-4 p-4'>
                            <Form.Control
                                type='text'
                                value={searchSent}
                                onChange={e => setSearchSent(e.target.value)}
                                placeholder={t('teacher.communications.searchPlaceholder')}
                                className='mb-3'
                                style={{ borderRadius: '10px', maxWidth: '400px' }}
                            />
                            {sentLoading ? (
                                <>
                                    {[1, 2].map(i => (
                                        <div key={i} className='border rounded-4 p-3 mb-3'>
                                            <div className='bg-light rounded-3' style={{ height: '14px', width: '50%' }} />
                                            <div className='bg-light rounded-3 mt-2' style={{ height: '12px', width: '90%' }} />
                                        </div>
                                    ))}
                                </>
                            ) : sentMessages.length === 0 ? (
                                <p className='text-muted text-center py-5 mb-0'>{t('teacher.communications.noSent')}</p>
                            ) : (
                                sentMessages.filter(m => matchesSearch(m, searchSent)).map(m => renderSentItem(m))
                            )}
                        </Card>
                    </Col>
                </Row>
            )}

            <Modal show={showComposeModal} onHide={() => setShowComposeModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className='fw-bold' style={{ fontSize: '20px' }}>{t('teacher.communications.newmessage')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleComposeSubmit}>
                    <Modal.Body>
                        {messageError2 && sendContext === 'compose' && (
                            <Alert variant='danger' dismissible onClose={() => dispatch(resetMessage())}>
                                {messageError2}
                            </Alert>
                        )}
                        <Form.Group className='mb-3'>
                            <Form.Label className='text-muted fw-semibold'>{t('teacher.communications.sendTo')}</Form.Label>
                            <Form.Select
                                value={composeForm.recipientType}
                                onChange={e => setComposeForm({ ...composeForm, recipientType: e.target.value, recipientId: '', groupId: '' })}
                                style={{ borderRadius: '10px' }}
                            >
                                <option value='individual'>{t('teacher.communications.individualAlumno')}</option>
                                <option value='group'>{t('teacher.communications.groupComplete')}</option>
                                <option value='noticeboard'>{t('teacher.communications.noticeboardTab')}</option>
                            </Form.Select>
                        </Form.Group>

                        {composeForm.recipientType === 'individual' && (
                            <>
                                <Form.Group className='mb-3'>
                                    <Form.Label className='text-muted fw-semibold'>{t('teacher.communications.student')}</Form.Label>
                                    <Form.Select
                                        value={composeForm.recipientId}
                                        onChange={e => setComposeForm({ ...composeForm, recipientId: e.target.value })}
                                        style={{ borderRadius: '10px' }}
                                        required
                                    >
                                        <option value=''>{t('teacher.communications.selectStudent')}</option>
                                        {(students || []).map(s => (
                                            <option key={s.id} value={s.id}>{s.name} {s.groupName ? `· ${s.groupName}` : ''}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                                <Form.Group className='mb-3'>
                                    <Form.Label className='text-muted fw-semibold'>{t('teacher.communications.recipientTarget')}</Form.Label>
                                    <Form.Select
                                        value={composeForm.recipientRole}
                                        onChange={e => setComposeForm({ ...composeForm, recipientRole: e.target.value })}
                                        style={{ borderRadius: '10px' }}
                                    >
                                        <option value='student'>{t('teacher.communications.toStudent')}</option>
                                        <option value='parent'>{t('teacher.communications.toParent')}</option>
                                    </Form.Select>
                                </Form.Group>
                            </>
                        )}

                        {composeForm.recipientType === 'group' && (
                            <Form.Group className='mb-3'>
                                <Form.Label className='text-muted fw-semibold'>{t('teacher.communications.group')}</Form.Label>
                                <Form.Select
                                    value={composeForm.groupId}
                                    onChange={e => setComposeForm({ ...composeForm, groupId: e.target.value })}
                                    style={{ borderRadius: '10px' }}
                                    required
                                >
                                    <option value=''>{t('teacher.communications.selectGroup')}</option>
                                    {(groups || []).map(g => (
                                        <option key={g.odooGroupId || g._id} value={g.odooGroupId || g._id}>{g.name} - {g.courseName}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        )}

                        <Form.Group className='mb-3'>
                            <Form.Label className='text-muted fw-semibold'>{t('teacher.communications.subject')}</Form.Label>
                            <Form.Control
                                type='text'
                                value={composeForm.subject}
                                onChange={e => setComposeForm({ ...composeForm, subject: e.target.value })}
                                style={{ borderRadius: '10px' }}
                                required
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label className='text-muted fw-semibold'>{t('teacher.communications.message')}</Form.Label>
                            <Form.Control
                                as='textarea'
                                rows={5}
                                value={composeForm.message}
                                onChange={e => setComposeForm({ ...composeForm, message: e.target.value })}
                                style={{ borderRadius: '10px' }}
                                required
                            />
                        </Form.Group>
                        <Form.Group className='mt-3'>
                            <Form.Label className='text-muted fw-semibold'>{t('teacher.communications.attachments')}</Form.Label>
                            {composeForm.attachments.length > 0 && (
                                <div className='mb-2'>
                                    {composeForm.attachments.map((a, idx) => (
                                        <div key={idx} className='d-flex justify-content-between align-items-center p-2 mb-1 bg-light rounded-3'>
                                            <span style={{ fontSize: '13px' }}>{a.name || a.url}</span>
                                            <Button variant='link' size='sm' className='text-danger p-0'
                                                onClick={() => setComposeForm({ ...composeForm, attachments: composeForm.attachments.filter((_, i) => i !== idx) })}>
                                                <i className='fa-solid fa-xmark'></i>
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className='d-flex align-items-center gap-2'>
                                <Form.Control
                                    type='file'
                                    multiple
                                    onChange={e => handleFileSelect(e, 'compose')}
                                    disabled={uploadingFiles}
                                    style={{ borderRadius: '10px' }}
                                />
                                {uploadingFiles && <span className='spinner-border spinner-border-sm text-secondary flex-shrink-0'></span>}
                            </div>
                            <p className='text-muted mb-0 mt-1' style={{ fontSize: '12px' }}>
                                {uploadingFiles ? t('teacher.communications.uploading') : t('teacher.communications.selectFiles')}
                            </p>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className='border-0'>
                        <Button variant='light' onClick={() => setShowComposeModal(false)} className='px-4'>
                            {t('global.cancel')}
                        </Button>
                        <Button type='submit' className='btn-primary px-4' disabled={messageSending}>
                            {messageSending ? (
                                <span className='spinner-border spinner-border-sm align-middle'></span>
                            ) : (
                                <>{t('teacher.communications.send')} <i className='fa-solid fa-paper-plane ms-1'></i></>
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <Modal show={!!activeThreadItem} onHide={handleCloseThread} centered scrollable>
                <Modal.Header closeButton>
                    <Modal.Title className='fw-bold' style={{ fontSize: '18px' }}>
                        {activeThreadItem?.last?.subject}
                        {activeThreadItem?.last?.recipientName && (
                            <span className='d-block text-muted fw-normal mt-1' style={{ fontSize: '14px' }}>
                                {t('teacher.communications.threadRecipient')} {activeThreadItem.last.recipientName}
                            </span>
                        )}
                        {activeThreadItem?.last?.groupName && !activeThreadItem?.last?.recipientName && (
                            <span className='d-block text-muted fw-normal mt-1' style={{ fontSize: '14px' }}>
                                {t('teacher.communications.threadGroup')} {activeThreadItem.last.groupName}
                            </span>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ backgroundColor: '#f9fafb' }}>
                    {threadLoading ? (
                        <div className='d-flex justify-content-center py-5'>
                            <span className='spinner-border text-secondary'></span>
                        </div>
                    ) : (
                        <>
                            <div className='d-flex flex-column gap-3'>
                                {(thread.length > 0 ? thread : (activeThreadItem ? [activeThreadItem.last] : [])).map(msg => {
                                    const mine = String(msg.sender) === String(clientauth?.userid)
                                    return (
                                        <div key={msg._id} className={`d-flex ${mine ? 'justify-content-end' : 'justify-content-start'}`}>
                                            <div className={`rounded-4 p-3 communication-bubble ${mine ? 'communication-bubble-mine' : 'communication-bubble-their'}`}>
                                                <p className='mb-1 fw-semibold' style={{ fontSize: '12px', opacity: 0.75 }}>
                                                    {msg.senderName || (mine ? t('teacher.communications.you' ) : '')} · {formatDate(msg.createdAt)} {formatTime(msg.createdAt)}
                                                </p>
                                                <p className='mb-0' style={{ fontSize: '14px', whiteSpace: 'pre-line' }}>{msg.message}</p>
                                                {msg.attachments && msg.attachments.length > 0 && (
                                                    <div className='mt-2 pt-2 border-top'>
                                                        {msg.attachments.map((a, idx) => (
                                                            <div key={idx} style={{ fontSize: '13px' }}>
                                                                {a.url ? <a href={a.url} target='_blank' rel='noreferrer'>{a.name || a.url}</a> : <span>{a.name}</span>}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>

                            <Form onSubmit={handleReplySubmit} className='mt-4 pt-3 border-top'>
                                {messageError2 && sendContext === 'reply' && (
                                    <Alert variant='danger' dismissible onClose={() => dispatch(resetMessage())}>
                                        {messageError2}
                                    </Alert>
                                )}
                                {replyAttachments.length > 0 && (
                                    <div className='mb-2'>
                                        {replyAttachments.map((a, idx) => (
                                            <div key={idx} className='d-flex justify-content-between align-items-center p-2 mb-1 bg-white rounded-3 border'>
                                                <span style={{ fontSize: '13px' }}>{a.name || a.url}</span>
                                                <Button variant='link' size='sm' className='text-danger p-0'
                                                    onClick={() => setReplyAttachments(replyAttachments.filter((_, i) => i !== idx))}>
                                                    <i className='fa-solid fa-xmark'></i>
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div className='d-flex align-items-center gap-2 mb-2'>
                                    <Form.Control
                                        type='file'
                                        multiple
                                        onChange={e => handleFileSelect(e, 'reply')}
                                        disabled={uploadingReplyFiles}
                                        style={{ borderRadius: '10px' }}
                                    />
                                    {uploadingReplyFiles && <span className='spinner-border spinner-border-sm text-secondary flex-shrink-0'></span>}
                                </div>
                                <div className='d-flex gap-2'>
                                    <Form.Control
                                        as='textarea'
                                        rows={2}
                                        value={replyText}
                                        onChange={e => setReplyText(e.target.value)}
                                        placeholder={t('teacher.communications.replyPlaceholder')}
                                        style={{ borderRadius: '10px' }}
                                    />
                                    <Button type='submit' className='btn-primary rounded-3 px-3 flex-shrink-0'
                                        disabled={messageSending || !replyText.trim()}>
                                        {messageSending && sendContext === 'reply' ? (
                                            <span className='spinner-border spinner-border-sm align-middle'></span>
                                        ) : (
                                            <i className='fa-solid fa-paper-plane'></i>
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </>
                    )}
                </Modal.Body>
            </Modal>

            <Modal show={!!activeNoticeItem} onHide={() => setActiveNoticeItem(null)} centered animation={false}>
                <Modal.Header closeButton>
                    <Modal.Title className='fw-bold' style={{ fontSize: '18px' }}>
                        {activeNoticeItem?.subject}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ fontSize: '14px', whiteSpace: 'pre-line', color: '#374151' }}>
                        {activeNoticeItem?.message}
                    </p>
                    {activeNoticeItem?.attachments && activeNoticeItem.attachments.length > 0 && (
                        <div className='mt-2'>
                            <p className='mb-1 fw-semibold' style={{ fontSize: '12px' }}>{t('teacher.communications.attachments')}:</p>
                            {activeNoticeItem.attachments.map((a, idx) => (
                                <div key={idx} style={{ fontSize: '13px' }}>
                                    {a.url ? <a href={a.url} target='_blank' rel='noreferrer'>{a.name || a.url}</a> : <span>{a.name}</span>}
                                </div>
                            ))}
                        </div>
                    )}
                    <p className='text-muted mt-3 mb-0' style={{ fontSize: '12px' }}>
                        {formatDate(activeNoticeItem?.createdAt)} · {formatTime(activeNoticeItem?.createdAt)}
                        {activeNoticeItem?.senderName && ` · ${activeNoticeItem.senderName}`}
                        {activeNoticeItem?.groupName && ` · ${activeNoticeItem.groupName}`}
                    </p>
                </Modal.Body>
                <Modal.Footer className='border-0'>
                    {activeNoticeItem && String(activeNoticeItem.sender) === String(clientauth?.userid) && (
                        <Button variant='outline-danger' className='me-auto'
                            onClick={() => {
                                Swal.fire({
                                    title: t('teacher.communications.deleteNotice'),
                                    text: t('teacher.communications.deleteNoticeText'),
                                    icon: 'warning',
                                    showCancelButton: true,
                                    confirmButtonColor: '#d33',
                                    cancelButtonColor: '#6c757d',
                                    confirmButtonText: t('teacher.communications.deleteConfirm')
                                }).then((result) => {
                                    if (result.isConfirmed) {
                                        const id = activeNoticeItem._id
                                        setActiveNoticeItem(null)
                                        dispatch(deleteMessage(id))
                                        toastSuccess(t('teacher.communications.noticeDeleted'))
                                    }
                                })
                            }}>
                            <i className='fa-solid fa-trash me-1'></i> {t('teacher.communications.delete')}
                        </Button>
                    )}
                    <Button variant='secondary' onClick={() => setActiveNoticeItem(null)}>
                        {t('global.close')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    )
}
